import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Badge, Button, Modal, useManagedModal } from "@shapr3d/frontend-lib";
import type React from "react";
import { useParams, useSearchParams } from "react-router-dom";

import useNavigation from "../../../../hooks/navigation";
import useAccount, { type UserID } from "../../../../hooks/useAccount";
import { useIntersectionObserver } from "../../../../hooks/useIntersectionObserver";
import { useProject } from "../../../../hooks/useProject";
import {
  isProjectAccessRequest,
  isProjectInvitation,
  isProjectMember,
  type ProjectAccessRequestID,
  type ProjectInvitationID,
  type ProjectUser,
  type ProjectUserAssignableRole,
  useDeleteProjectInvitation,
  useDeleteProjectUser,
  useProjectSharing,
  useRejectProjectAccessRequest,
} from "../../../../hooks/useProjectSharing";
import { useTeamActiveSubscription } from "../../../../hooks/useTeamActiveSubscription";
import { useTeam } from "../../../../hooks/useTeams";
import { useTracker } from "../../../../hooks/useTracker";
import { ElementClickAction, ElementClickLocation } from "../../../../lib/TrackingAdapter";
import {
  ProjectRoleUtils,
  SearchParam,
  SearchParamIntent,
  SubscriptionPeriod,
} from "../../../../types";
import { CopyLinkButton } from "./components/CopyLinkButton";
import type { EmailInvitationFormHandle } from "./components/EmailInvitationForm";
import { EmailInvitationFormProject } from "./components/EmailInvitationFormProject";
import { InvitationRowProject } from "./components/InvitationRowProject";
import { MemberRowProject } from "./components/MemberRowProject";
import { OverlayProvider } from "./components/MenuPortal";
import { PaywallModal } from "./components/PaywallModal";
import { RemoveConfirmationModal } from "./components/RemoveConfirmationModal";
import { RequestAccessRowProject } from "./components/RequestAccessRowProject";
import { Separator } from "./components/Separator";
import { ShareList } from "./components/ShareList";
import type { ShareRowRef } from "./components/ShareRow";
import { SpaceRow } from "./components/SpaceRow";

import style from "./ShareProjectModal.module.css";

const formatNotificationCount = (count: number, cap = 99) => {
  return count > cap ? `${cap}+` : String(count);
};

export const ShareProjectModal: React.FC = () => {
  const tracker = useTracker();
  const { projectId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const userToRemoveRef = useRef<UserID | null>(null);
  const invitationToRemoveRef = useRef<ProjectInvitationID | null>(null);
  const accessRequestToRemoveRef = useRef<ProjectAccessRequestID | null>(null);
  const emailInvitationFormRef = useRef<EmailInvitationFormHandle>(null);

  const emailListContainerRef = useRef<HTMLDivElement>(null);
  const sharingRowRefs = useRef<ShareRowRef[]>([]);

  const consumeShareIntent = useCallback(() => {
    setSearchParams(
      (searchParams) => {
        searchParams.delete(SearchParam.Intent);
        return searchParams;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const { data: projectData } = useProject();
  const { teamData } = useTeam(projectData?.teamId);

  const onClose = useCallback(() => {
    setAcceptedUserIds(new Set());
    tracker.trackShareModalVisibilityChanged({
      remoteProjectId: projectId,
      sharedResource: "project",
      visible: false,
      spaceId: projectData?.space?.spaceId,
      teamUserRole: teamData?.role,
      projectUserRole: projectData?.userRole,
    });
    consumeShareIntent();
  }, [tracker, projectId, projectData, teamData?.role, consumeShareIntent]);

  const {
    data: projectSharingData,
    refetch: refetchProjectSharingData,
    isLoading: isProjectSharingDataLoading,
  } = useProjectSharing();
  const { data: accountData } = useAccount();

  const isOwner = Boolean(projectData && ProjectRoleUtils.isAtLeast(projectData.userRole, "owner"));

  const shareModal = useManagedModal({
    onClose: onClose,
  });
  const navigation = useNavigation();

  const paywallReturnURL = useMemo(() => {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set(SearchParam.Intent, SearchParamIntent.Share);
    return currentUrl.toString();
  }, []);

  useEffect(() => {
    if (
      searchParams.get(SearchParam.Intent) !== SearchParamIntent.Share ||
      isProjectSharingDataLoading
    ) {
      return;
    }
    shareModal.open();
  }, [isProjectSharingDataLoading, searchParams, shareModal]);

  const removeProjectUserModal = useManagedModal();
  const removeProjectInvitationModal = useManagedModal();
  const removeAccessRequestModal = useManagedModal();
  const ownerEditorPaywallModal = useManagedModal();
  const nonOwnerEditorPaywallModal = useManagedModal();

  const containerIntersectionObserver = useIntersectionObserver(
    emailListContainerRef,
    sharingRowRefs
  );

  const {
    mutateAsync: mutateDeleteProjectInvitation,
    isPending: isDeleteProjectInvitationPending,
    isError: isDeleteProjectInvitationError,
    removeInvitationFromCache,
  } = useDeleteProjectInvitation({
    onSuccess: async () => {
      removeProjectInvitationModal.close();
      if (!invitationToRemoveRef.current) return;
      removeInvitationFromCache(invitationToRemoveRef.current);
    },
  });

  const {
    mutateAsync: mutateDeleteProjectUser,
    isPending: isDeleteProjectUserPending,
    isError: isDeleteProjectUserError,
    removeUserFromCache,
  } = useDeleteProjectUser({
    onSuccess: async () => {
      removeProjectUserModal.close();
      if (!userToRemoveRef.current) return;
      removeUserFromCache(userToRemoveRef.current);
    },
  });

  const {
    mutateAsync: mutateRejectProjectAccessRequest,
    isPending: isRejectAccessRequestPending,
    isError: isRejectAccessRequestError,
  } = useRejectProjectAccessRequest({
    onSuccess: async () => {
      removeAccessRequestModal.close();
      if (!accessRequestToRemoveRef.current) return;
    },
  });

  const [acceptedUserIds, setAcceptedUserIds] = useState<Set<UserID>>(() => new Set());

  const orderedUsers = useMemo(() => {
    const accessRequests = projectSharingData?.accessRequests ?? [];
    const invitations = projectSharingData?.invitations ?? [];
    const membersAll = projectSharingData?.members ?? [];

    const acceptedMembers = membersAll.filter((member) => acceptedUserIds.has(member.userId));
    const members = membersAll.filter((member) => !acceptedUserIds.has(member.userId));

    const userList: ProjectUser[] = [
      ...accessRequests,
      ...acceptedMembers,
      ...invitations,
      ...members,
    ];

    const rank = (user: ProjectUser) => {
      if (isProjectAccessRequest(user)) return 0;
      if (isProjectMember(user) && acceptedUserIds.has(user.userId)) return 0;
      if (isProjectInvitation(user)) return 1;
      return 2;
    };

    return userList.sort((a, b) => {
      const rankA = rank(a);
      const rankB = rank(b);
      if (rankA !== rankB) return rankA - rankB;
      if (isProjectMember(a) && isProjectMember(b)) {
        return a.email.localeCompare(b.email);
      }
      return b.createdAt.localeCompare(a.createdAt);
    });
  }, [
    projectSharingData?.accessRequests,
    projectSharingData?.invitations,
    projectSharingData?.members,
    acceptedUserIds,
  ]);

  const modalTitle = useMemo(() => {
    if (projectData?.project.name) {
      return `Share "${projectData.project.name}"`;
    }
    return "Share this Project";
  }, [projectData?.project.name]);

  const { data: isTeamSubscribed, isLoading: isSubscriptionDataLoading } =
    useTeamActiveSubscription(projectData?.teamId);
  const isProRoleEligible = useCallback(
    (_: ProjectUserAssignableRole) => {
      if (isTeamSubscribed === undefined) return false;
      if (isTeamSubscribed) return true;
      if (isOwner) {
        ownerEditorPaywallModal.open();
      } else {
        nonOwnerEditorPaywallModal.open();
      }
      if (projectId) {
        tracker.trackPaywallModalShown({
          paywallReason: "project_editor_role",
          remoteProjectId: projectId,
          hasSubscribeAction: isOwner,
        });
      }
      return false;
    },
    [
      isOwner,
      isTeamSubscribed,
      nonOwnerEditorPaywallModal,
      ownerEditorPaywallModal,
      projectId,
      tracker,
    ]
  );

  const notificationCount = projectSharingData?.accessRequests.length ?? 0;

  return (
    <>
      <Button
        onClick={async () => {
          try {
            await refetchProjectSharingData();
            shareModal.open();
            tracker.trackShareModalVisibilityChanged({
              remoteProjectId: projectId,
              sharedResource: "project",
              visible: true,
              spaceId: projectData?.space?.spaceId,
              teamUserRole: teamData?.role,
              projectUserRole: projectData?.userRole,
            });
          } catch {
            //ignore error
          }
        }}
      >
        Share
        {notificationCount > 0 && (
          <Badge variant="error" radius="full" className={style.notificationBadge}>
            {formatNotificationCount(notificationCount)}
          </Badge>
        )}
      </Button>
      {projectSharingData && (
        <Modal
          {...shareModal.props}
          className={style.modal}
          title={modalTitle}
          onOpen={() => {
            emailInvitationFormRef.current?.focus();
          }}
          onCloseStart={() => {
            emailInvitationFormRef.current?.blur();
          }}
        >
          <OverlayProvider>
            <div className={style.container}>
              <EmailInvitationFormProject
                ref={emailInvitationFormRef}
                isProRoleEligible={isProRoleEligible}
                isDisabled={isSubscriptionDataLoading}
              />
              <span className={style.sectionTitle}>Who has access</span>
              {(() => {
                if (!projectSharingData || !projectData) {
                  return null;
                }

                if (!isOwner || !projectSharingData?.space) {
                  return (
                    <SpaceRow
                      variant="no-access"
                      projectName={projectData?.project.name ?? "Untitled Project"}
                    />
                  );
                }
                if (projectSharingData.space.spaceType === "private") {
                  return (
                    <SpaceRow
                      variant={"owner-private"}
                      space={projectSharingData.space}
                      projectName={projectData.project.name}
                    />
                  );
                } else {
                  return (
                    <SpaceRow
                      variant="owner-team"
                      space={projectSharingData.space}
                      projectName={projectData.project.name}
                    />
                  );
                }
              })()}
              <ShareList className={style.emailListContainer} ref={emailListContainerRef}>
                {orderedUsers.map((user, index) => (
                  <Fragment key={user.email}>
                    <Separator />
                    {isProjectAccessRequest(user) ? (
                      <RequestAccessRowProject
                        ref={(ref) => {
                          if (ref) {
                            sharingRowRefs.current[index + 1] = ref;
                          }
                        }}
                        request={user}
                        containerIntersectionObserver={containerIntersectionObserver}
                        onAccepted={(userId, role) => {
                          tracker.trackProjectPageAccessRequestAccepted({
                            projectId: projectId ?? "",
                            projectTeamId: projectData?.teamId ?? "",
                            role: role,
                          });

                          setAcceptedUserIds((prev) => {
                            const next = new Set(prev);
                            next.add(userId);
                            return next;
                          });
                        }}
                        isProRoleEligible={isProRoleEligible}
                        onRemove={(accessRequestId) => {
                          accessRequestToRemoveRef.current = accessRequestId;
                          removeAccessRequestModal.open();
                        }}
                        isDisabled={isSubscriptionDataLoading}
                      />
                    ) : isProjectInvitation(user) ? (
                      <InvitationRowProject
                        ref={(ref) => {
                          if (ref) {
                            sharingRowRefs.current[index + 1] = ref;
                          }
                        }}
                        invitation={user}
                        containerIntersectionObserver={containerIntersectionObserver}
                        onRemove={(invitationId) => {
                          invitationToRemoveRef.current = invitationId;
                          removeProjectInvitationModal.open();
                        }}
                        isProRoleEligible={isProRoleEligible}
                        isDisabled={isSubscriptionDataLoading}
                      />
                    ) : isProjectMember(user) ? (
                      <MemberRowProject
                        ref={(ref) => {
                          if (ref) {
                            sharingRowRefs.current[index + 1] = ref;
                          }
                        }}
                        member={user}
                        containerIntersectionObserver={containerIntersectionObserver}
                        onRemove={(userId) => {
                          userToRemoveRef.current = userId;
                          removeProjectUserModal.open();
                        }}
                        isProRoleEligible={isProRoleEligible}
                        isDisabled={isSubscriptionDataLoading}
                        isCurrentUser={user.userId === accountData?.id}
                      />
                    ) : null}
                  </Fragment>
                ))}
              </ShareList>

              <div className={style.footerContainer}>
                <CopyLinkButton
                  onCopy={() => {
                    tracker.trackButtonClick({
                      action: ElementClickAction.COPY_LINK,
                      location: ElementClickLocation.SHARE_MODAL,
                      details: "project_link",
                    });
                  }}
                />
              </div>
            </div>
          </OverlayProvider>
        </Modal>
      )}
      <RemoveConfirmationModal
        entity={"invitation"}
        modalProps={removeProjectInvitationModal.props}
        isPending={isDeleteProjectInvitationPending}
        onConfirm={async () => {
          if (!invitationToRemoveRef.current) return;
          tracker.trackShareModalMemberRemoveConfirmed({
            remoteProjectId: projectId,
            invitationId: invitationToRemoveRef.current,
            sharedResource: "project",
            spaceId: projectData?.space?.spaceId,
            teamUserRole: teamData?.role,
            projectUserRole: projectData?.userRole,
          });
          return mutateDeleteProjectInvitation({ invitationId: invitationToRemoveRef.current });
        }}
        isError={isDeleteProjectInvitationError}
      />
      <RemoveConfirmationModal
        entity={userToRemoveRef.current === accountData?.id ? "project" : "user"}
        modalProps={removeProjectUserModal.props}
        isPending={isDeleteProjectUserPending}
        onConfirm={async () => {
          if (!userToRemoveRef.current) return;
          tracker.trackShareModalMemberRemoveConfirmed({
            remoteProjectId: projectId,
            affectedUserId: userToRemoveRef.current,
            sharedResource: "project",
            spaceId: projectData?.space?.spaceId,
            teamUserRole: teamData?.role,
            projectUserRole: projectData?.userRole,
          });
          return mutateDeleteProjectUser({ userId: userToRemoveRef.current });
        }}
        isError={isDeleteProjectUserError}
        action={userToRemoveRef.current === accountData?.id ? "leave" : "remove"}
      />

      <RemoveConfirmationModal
        entity={"access request"}
        modalProps={removeAccessRequestModal.props}
        isPending={isRejectAccessRequestPending}
        onConfirm={async () => {
          if (!accessRequestToRemoveRef.current) return;
          tracker.trackProjectPageAccessRequestRejected({
            projectId: projectId ?? "",
            projectTeamId: projectData?.teamId ?? "",
          });
          return mutateRejectProjectAccessRequest({
            projectAccessRequestId: accessRequestToRemoveRef.current,
          });
        }}
        isError={isRejectAccessRequestError}
        action={"deny"}
      />

      <PaywallModal
        modalProps={ownerEditorPaywallModal.props}
        title={"Subscribe to collaborate on projects"}
        description={"Invite others to edit and keep projects up to date."}
        showSubscriptionButton={true}
        onClose={() => {
          if (!projectId) return;
          tracker.trackPaywallModalDismissed({
            paywallReason: "project_editor_role",
            remoteProjectId: projectId,
          });
        }}
        onClick={() => {
          navigation.goToCheckout({
            period: SubscriptionPeriod.Yearly,
            showPeriodPicker: true,
            utmSource: "project_editor_role",
            returnToURL: paywallReturnURL,
          });
          if (!projectId) return;
          tracker.trackPaywallSubscribeClicked({
            paywallReason: "project_editor_role",
            remoteProjectId: projectId,
          });
        }}
      />
      <PaywallModal
        modalProps={nonOwnerEditorPaywallModal.props}
        size="medium"
        title={"Your current team plan doesn’t include this feature"}
        description={
          "Contact your team owner to upgrade the subscription and gain access to all Pro features."
        }
        onClose={() => {
          if (!projectId) return;
          tracker.trackPaywallModalDismissed({
            paywallReason: "project_editor_role",
            remoteProjectId: projectId,
          });
        }}
        showSubscriptionButton={false}
      />
    </>
  );
};
